import React, { useContext, useEffect, useState } from 'react'
import { AuthTokenContext } from '../../contexts/AuthTokenContext'
import { UserInfoContext } from '../../contexts/UserInfoContext'
import { useSnackbar } from 'notistack'
import {
  Box,
  Card, CardHeader,
  Grid, IconButton,
  Tab,
  Typography
} from '@mui/material'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import TabContext from '@mui/lab/TabContext'
import { ViewProfileAvatar } from '../../commonUIComponents/CustomAvatar'
import userProfileService from '../../../services/userProfileService'
import CompactPostCard from './CompactPostCard'
import CompactCommentCard from './CompactCommentCard'
import UpdateAvatarPage from '../UpdateAvatarPage/UpdateAvatarPage'
import EditIcon from '@mui/icons-material/Edit'
import UpdateNicknamePage from '../UpdateNicknamePage'
import loginService from '../../../services/loginService'
import { SearchBarContext } from '../../contexts/SearchBarContext'

const ViewProfilePage = () => {
  const { authToken, setAuthToken } = useContext(AuthTokenContext)
  const { userInfo, setUserInfo } = useContext(UserInfoContext)
  const { setCommunityId } = useContext(SearchBarContext)
  const { enqueueSnackbar } = useSnackbar()
  const [tabValue, setTabValue] = React.useState('overview')
  const [pagination, setPagination] = useState(0)
  const [pageSize, setPageSize] = useState(20)
  const [isLastPage, setIsLastPage] = useState(false)
  const [posts, setPosts] = useState()
  const [comments, setComments] = useState()
  const [openUpdateAvatarModal, setOpenUpdateAvatarModal] = useState(false)
  const [openUpdateNicknameModal, setOpenUpdateNicknameModal] = useState(false)
  const [loading, setLoading] = useState(true)

  const fetchUserPosts = () => {
    if (userInfo && userInfo?.id) {
      setLoading(true)
      userProfileService.fetchPostsByUserId(pagination, pageSize, userInfo?.id)
        .then(res => {
          if (posts) {
            setPosts([...posts, ...res.posts])
          } else {
            setPosts(res.posts)
          }
          setIsLastPage(res.pagination.isLastPage)
        })
        .catch(err => enqueueSnackbar(err, { variant: 'error' }))
        .finally(() => setLoading(false))
    }
  }

  const fetchUserComments = () => {
    if (userInfo && userInfo?.id) {
      setLoading(true)
      userProfileService.fetchCommentsByUserId(pagination, pageSize, userInfo?.id)
        .then(res => {
          if (comments) {
            setComments([...comments, ...res.comments])
          } else {
            setComments(res.comments)
          }
          setIsLastPage(res.pagination.isLastPage)
        })
        .catch(err => enqueueSnackbar(err, { variant: 'error' }))
        .finally(() => setLoading(false))
    }
  }

  useEffect(() => {
    setCommunityId(userInfo?.nickname)
    fetchUserPosts()
    fetchUserComments()
  }, [pagination, authToken])

  const tabHandler = (event, newValue) => {
    setTabValue(newValue)
  }

  const updateAvatarModalOpenHandler = () => setOpenUpdateAvatarModal(true)

  const updateAvatarModalCloseHandler = () => setOpenUpdateAvatarModal(false)

  const updateNicknameModalOpenHandler = () => setOpenUpdateNicknameModal(true)

  const updateNicknameModalCloseHandler = () => setOpenUpdateNicknameModal(false)

  const fetchUserInfo = () => {
    userProfileService.refreshUserToken(userInfo.id)
      .then(res => {
        loginService.setToken(res.token)
        setAuthToken(res.token)
        loginService.setUser(JSON.stringify(res.user))
        setUserInfo(JSON.stringify(res.user))
      })
      .catch(err => enqueueSnackbar(err.response?.data.message, { variant: 'warning' }))
  }

  const ProfileCard = () => {
    return (
      <Grid item xs={12}>
        <Card elevation={0} sx={{ marginY: 2 }}>
          <CardHeader
            avatar={
              <IconButton onClick={updateAvatarModalOpenHandler} sx={{ p: 0 }}>
                <ViewProfileAvatar src={userInfo.avatarImageUrl} nickname={userInfo.nickname} />
              </IconButton>
            }
            titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
            title={
              <>
                {userInfo.nickname}
                <IconButton onClick={updateNicknameModalOpenHandler} sx={{ p: 0, pl: '0.5rem' }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </>
            }
            subheaderTypographyProps={{
              fontSize: 15
            }}
            subheader={
              <>
                <Typography variant='subtitle' sx={{ fontWeight: 'bold' }}>
                  u/{userInfo.nickname}
                </Typography>
              </>
            }
          />
        </Card>
      </Grid>
    )
  }

  const ViewDataList = () => {
    return (
      <Grid item xs>
        <TabContext value={tabValue}>
          <Box>
            <TabList onChange={tabHandler} aria-label="profile-tab">
              <Tab label="Overview" value="overview" sx={{ fontWeight: 'bold' }}/>
              <Tab label="Posts" value="posts" sx={{ fontWeight: 'bold' }}/>
              <Tab label="Comments" value="comments" sx={{ fontWeight: 'bold' }}/>
              <Tab label="Saved" value="saved" sx={{ fontWeight: 'bold' }} disabled/>
            </TabList>
          </Box>
          <Box>
            <TabPanel value="overview">
              <Grid container spacing={2}>
              </Grid>
            </TabPanel>
            <TabPanel value="posts">
              <Grid container>
                 {posts?.map(post => <CompactPostCard key={post.id} post={post} fetchPosts={fetchUserPosts}/>)}
              </Grid>
            </TabPanel>
            <TabPanel value="comments">
              <Grid container>
                {comments?.map(comment => <CompactCommentCard key={comment.id} comment={comment} fetchComments={fetchUserComments}/>)}
              </Grid>
            </TabPanel>
            <TabPanel value="saved">
              <Grid container>
              </Grid>
            </TabPanel>
          </Box>
        </TabContext>
      </Grid>
    )
  }

  const UserInformation = () => {
    return (
      <Grid item xs="auto" sx={{ display: { xs: 'none', md: 'block' } }}>
        <Box
          elevation={1}
          sx={{
            overflow: 'hidden',
            objectFit: 'contain',
            borderRadius: '8px',
            maxWidth: 316,
            width: 1,
            border: '1px solid'
          }}
        />
      </Grid>
    )
  }

  return (
    <>
      { authToken && userInfo
        ? (
          <Grid container justifyContent="space-between">
            <ProfileCard />
            <ViewDataList />
            <UserInformation />
            {openUpdateNicknameModal && <UpdateNicknamePage open={openUpdateNicknameModal} onClose={updateNicknameModalCloseHandler} submitOnSuccess={fetchUserInfo} />}
            {openUpdateAvatarModal && <UpdateAvatarPage open={openUpdateAvatarModal} onClose={updateAvatarModalCloseHandler} submitOnSuccess={fetchUserInfo} />}
          </Grid>
          )
        : undefined
      }
    </>
  )
}

export default ViewProfilePage
